import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getDownloadURL, listAll, ref } from '@firebase/storage'
import type { Vehicle } from '@otw/models'
import {
  campaignFeaturedVehiclePhotoFileNames,
  campaignFeaturedVehiclePhotoFolder,
} from '@otw/models/leads/campaignFeaturedVehicles'
import { storage } from 'config/firebase'
import {
  fetchMasterInventory,
  SEARCH_CACHE_STALE_MS,
  SEARCH_QUERY_KEYS,
} from 'hooks/search/searchCaches'

const IMAGE_FILE = /\.(jpe?g|png|webp|gif)$/i

/** Public download URL for the card preview. Main photo first, then any image in the folder. */
export async function resolveCampaignVehiclePhotoUrl(vehicle: Vehicle): Promise<string | null> {
  const data: Record<string, unknown> = {
    photos: vehicle.photos,
    photo_storage_path: vehicle.photo_storage_path,
  }
  const folder = campaignFeaturedVehiclePhotoFolder(vehicle.id, data)
  for (const name of campaignFeaturedVehiclePhotoFileNames(data)) {
    try {
      return await getDownloadURL(ref(storage, `${folder}/${name}`))
    } catch {
      // Try the next named slot, then the folder.
    }
  }
  if (!vehicle.photo_storage_path?.trim()) return null
  try {
    const listed = await listAll(ref(storage, folder))
    const image = listed.items.find((item) => IMAGE_FILE.test(item.name))
    if (!image) return null
    return await getDownloadURL(image)
  } catch {
    return null
  }
}

const inventoryVehicleLabel = (vehicle: Vehicle): string => {
  const title = [vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(' ')
  const stock = vehicle.stock_number?.trim()
  return stock ? `${title || 'Truck'} · ${stock}` : title || vehicle.id
}

/** All master inventory, including trucks that are not on the website. */
export const useCampaignInventoryVehicles = (enabled = true) => {
  const queryResult = useQuery({
    queryKey: SEARCH_QUERY_KEYS.inventory,
    enabled,
    queryFn: fetchMasterInventory,
    staleTime: SEARCH_CACHE_STALE_MS,
  })

  const vehicles = useMemo(
    () =>
      [...(queryResult.data ?? [])].sort((a, b) =>
        inventoryVehicleLabel(a).localeCompare(inventoryVehicleLabel(b), 'en'),
      ),
    [queryResult.data],
  )

  return {
    vehicles,
    isLoading: queryResult.isLoading,
  }
}
